import { Component, ChangeDetectorRef } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { ToastService } from '../../shared/services/toast.service';

interface SecurityQuestion {
  questionId: number;
  question: string;
}

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  standalone: false,
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent {
  email: string = '';
  otp: any;
  newPassword: string = '';
  confirmPassword: string = '';
  errorMessage: string = '';
  showOtpFields: boolean = false;
  showSecurityQuestions: boolean = false;
  isOtpVerified: boolean = false;
  isSecurityVerified: boolean = false;
  isPasswordReset: boolean = false;
  isEmailDisabled: boolean = false;
  newPasswordType: string = 'password';
  confirmPasswordType: string = 'password';
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;
  resendTimer: number = 60;
  canResend: boolean = false;
  intervalId: any;

  securityQuestions: SecurityQuestion[] = [];
  securityAnswers: { questionId: number; answer: string }[] = [];

  constructor(
    private api: ApiService,
    private toast: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  sendOtp() {
    const vm = this;
    if (!vm.email) {
      vm.errorMessage = 'Please enter your email.';
      return;
    }
    vm.errorMessage = '';
    vm.api.postData('/partners/noAuth/partner-users/forget', { email: vm.email }).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          if (res.data && res.data.requiresSecurityVerification) {
            vm.securityQuestions = res.data.securityQuestions || [];
            vm.showSecurityQuestions = true;
            vm.securityAnswers = vm.securityQuestions.map(q => ({
              questionId: q.questionId,
              answer: ''
            }));
            vm.toast.show('Please answer security questions to proceed', 'info');
          } else {
            vm.showOtpFields = true;
            vm.isEmailDisabled = true;
            vm.startResendTimer();
            vm.toast.show(res.message || 'OTP sent successfully', 'success');
          }
        } else {
          vm.errorMessage = res?.message || 'Failed to send OTP';
        }
      },
      error: (err) => {
        vm.errorMessage = (typeof err === 'string' ? err : err?.message) || 'Failed to send OTP';
      }
    });
  }

  verifySecurityAnswers() {
    const vm = this;
    const validAnswers = vm.securityAnswers.filter(a => a.answer && a.answer.trim());
    if (validAnswers.length < 2) {
      vm.errorMessage = 'Please answer at least 2 security questions';
      return;
    }
    vm.errorMessage = '';
    vm.api.postData('/partners/noAuth/security-questions/verify', {
      email: vm.email,
      answers: vm.securityAnswers
    }).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          vm.isSecurityVerified = true;
          vm.showSecurityQuestions = false;
          vm.showOtpFields = true;
          vm.startResendTimer();
          vm.toast.show('Security answers verified! OTP sent to your email', 'success');
        } else {
          vm.errorMessage = res?.message || 'Invalid security answers';
        }
      },
      error: (err) => {
        vm.errorMessage = (typeof err === 'string' ? err : err?.message) || 'Failed to verify security answers';
      }
    });
  }

  onShowPassword(field) {
    const vm = this;
    if (field === 'new') {
      vm.showNewPassword = !vm.showNewPassword;
      vm.newPasswordType = vm.showNewPassword ? 'text' : 'password';
    } else {
      vm.showConfirmPassword = !vm.showConfirmPassword;
      vm.confirmPasswordType = vm.showConfirmPassword ? 'text' : 'password';
    }
  }

  validatePassword(password: string): string[] {
    const errors: string[] = [];
    if (password.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(password)) errors.push('1 uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('1 lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('1 number');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push('1 special character');
    return errors;
  }

  isPasswordValid(password: string): boolean {
    return this.validatePassword(password).length === 0;
  }

  hasUpperCase(password: string): boolean {
    return /[A-Z]/.test(password);
  }

  hasLowerCase(password: string): boolean {
    return /[a-z]/.test(password);
  }

  hasNumber(password: string): boolean {
    return /[0-9]/.test(password);
  }

  hasSpecialChar(password: string): boolean {
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  }

  verifyOtp() {
    const vm = this;
    if (!vm.otp) {
      vm.errorMessage = 'Please enter OTP';
      return;
    }
    vm.errorMessage = '';
    vm.api.postData('/partners/noAuth/partner-users/verify', { email: vm.email, otp: vm.otp }).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          vm.errorMessage = '';
          vm.isOtpVerified = true;
          vm.showOtpFields = false;
          vm.stopResendTimer();
          vm.toast.show('OTP verified successfully!', 'success');
        } else {
          vm.errorMessage = res?.message || 'Invalid OTP';
        }
      },
      error: (err) => {
        vm.errorMessage = (typeof err === 'string' ? err : err?.message) || 'Invalid OTP';
      }
    });
  }

  startResendTimer() {
    const vm = this;
    vm.resendTimer = 60;
    vm.canResend = false;
    if (vm.intervalId) {
      clearInterval(vm.intervalId);
    }
    vm.intervalId = setInterval(() => {
      if (vm.resendTimer > 0) {
        vm.resendTimer--;
      } else {
        vm.canResend = true;
        clearInterval(vm.intervalId);
      }
      vm.cdr.detectChanges();
    }, 1000);
  }

  stopResendTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  resendOtp() {
    const vm = this;
    if (vm.canResend) {
      vm.otp = '';
      vm.errorMessage = '';
      vm.sendOtp();
    }
  }

  resetPassword() {
    const vm = this;
    if (vm.newPassword !== vm.confirmPassword) {
      vm.errorMessage = 'Passwords do not match.';
      return;
    }
    if (!vm.isPasswordValid(vm.newPassword)) {
      const errors = vm.validatePassword(vm.newPassword);
      vm.errorMessage = 'Password must have: ' + errors.join(', ');
      return;
    }
    if (!vm.otp) {
      vm.errorMessage = 'Please enter OTP';
      return;
    }
    vm.errorMessage = '';

    const payload: any = {
      email: vm.email,
      otp: vm.otp,
      newPassword: vm.newPassword
    };

    if (vm.isSecurityVerified || vm.securityAnswers.length > 0) {
      payload.securityAnswers = vm.securityAnswers;
    }

    vm.api.postData('/partners/noAuth/partner-users/reset', payload).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          vm.isPasswordReset = true;
          vm.stopResendTimer();
          vm.toast.show('Password reset successfully!', 'success');
        } else {
          vm.errorMessage = res?.message || 'Failed to reset password';
        }
      },
      error: (err) => {
        vm.errorMessage = (typeof err === 'string' ? err : err?.message) || 'Failed to reset password';
      }
    });
  }
}
